/**
 * One flat shape for a lead row, and the tone maps the three views share.
 *
 * The API nests — `status.name`, `owner.full_name`, `source.name`,
 * `company_name` — while `mock/leads.fixture.js` is flat and uses the
 * prototype's own field names (`name`, `company`, `status` as a string). The
 * screens used to read the fixture's names, which meant every column but the
 * score rendered blank the moment §4 went live. Normalising once, here, is what
 * lets the table, the split list and the kanban card all read `row.name`
 * without any of them caring which shape arrived.
 *
 * `raw` is kept on the row because mutations need `id` and `row_version`, and
 * `status_id` / `source_id` are what a PATCH takes — the display name is not.
 */
import { dueLabel } from '../../utils/formatters.js';

/** Status name → the prototype's pill class. Its own map, not the API colour. */
export const STATUS_PILL = {
  New: 'pill-brand',
  Working: 'pill-sky',
  Nurturing: 'pill-violet',
  Qualified: 'pill-green',
  Unqualified: 'pill-amber',
  Converted: 'pill-teal',
  Junk: 'pill-gray',
};

/**
 * Score → `.score-hot` / `.score-warm` / `.score-cold`.
 *
 * Off the score, not off `score_band`, so the chip's colour and the number
 * inside it can never disagree — a lead scored 91 whose stored band still says
 * Warm because the rules changed since it was last scored would otherwise be an
 * amber chip reading 91.
 */
export function scoreClass(score) {
  if (score == null) return 'score-cold';
  if (score >= 75) return 'score-hot';
  if (score >= 50) return 'score-warm';
  return 'score-cold';
}

/** Kanban columns, in lifecycle order, with the prototype's stage colours. */
export const BOARD_COLUMNS = [
  ['New', '#6366f1'],
  ['Working', '#0ea5e9'],
  ['Nurturing', '#8b5cf6'],
  ['Qualified', '#10b981'],
  ['Unqualified', '#f59e0b'],
];

/** `dueLabel`, corrected for a follow-up already past on the day it is due. */
function followupLabel(lead) {
  if (!lead.next_followup_at) return null;
  const label = dueLabel(lead.next_followup_at);
  return lead.is_overdue && label === 'Today' ? 'Overdue today' : label;
}

/**
 * An API lead — or a fixture one — as the flat row the views render.
 *
 * @param {object} lead
 * @returns {object}
 */
export function toRow(lead) {
  /* The fixture path. `status` is a plain string there and there is no
     `row_version`, which is also the signal that nothing on this row is
     editable: a PATCH needs a version to send in If-Match. */
  if (typeof lead.status === 'string') {
    return {
      id: lead.id,
      lead_no: lead.id,
      name: lead.name,
      title: lead.title ?? null,
      company: lead.company ?? null,
      email: lead.email ?? null,
      phone: lead.phone ?? null,
      source: lead.source ?? null,
      partner: null,
      score: lead.score ?? null,
      rating: lead.rating ?? null,
      status: lead.status,
      value: lead.value ?? null,
      currency: 'USD',
      owner: lead.owner === 'Unassigned' ? null : lead.owner,
      followupLabel: lead.followup ?? null,
      followupAt: null,
      overdue: Boolean(lead.overdue),
      industry: lead.industry ?? null,
      country: lead.country ?? null,
      converted: lead.status === 'Converted',
      editable: false,
      campaign: lead.campaign,
      employees: lead.employees,
      engagement: lead.opens === undefined ? null : {
        email_opens: lead.opens,
        email_replies: lead.replies,
        website_visits: lead.visits,
        meetings_held: lead.meetings,
      },
      raw: lead,
    };
  }

  const status = lead.status?.name ?? null;
  return {
    id: lead.id,
    lead_no: lead.lead_no,
    name: lead.full_name,
    title: lead.job_title ?? null,
    company: lead.company_name ?? null,
    email: lead.email ?? null,
    phone: lead.phone ?? null,
    source: lead.source?.name ?? null,
    partner: lead.referred_by_partner?.name ?? null,
    score: lead.score ?? null,
    rating: lead.rating ?? lead.score_band ?? null,
    status,
    value: lead.estimated_value ?? null,
    currency: lead.currency ?? 'USD',
    owner: lead.owner?.full_name ?? null,
    /* `dueLabel` gives the prototype's own wording — Today, Tomorrow,
       Overdue 2d — from the timestamp the column actually stores.
       
       One correction on top of it: `dueLabel` compares whole days, so a
       follow-up at 09:00 that it is now 14:00 past comes back as "Today". The
       API says `is_overdue` (it compares `next_followup_at < now()`), and a
       rose pill reading "Today" invites exactly the wrong conclusion — that
       there is still time. The Overdue quick filter returns these rows, so the
       label has to agree with the filter that selected them. */
    followupLabel: followupLabel(lead),
    followupAt: lead.next_followup_at ?? null,
    overdue: Boolean(lead.is_overdue),
    slaBreached: Boolean(lead.is_sla_breached),
    industry: lead.industry?.name ?? null,
    country: lead.country?.name ?? null,
    employees: lead.employee_count ?? null,
    engagement: lead.engagement ?? null,
    converted: Boolean(lead.status?.is_converted),
    deadEnd: Boolean(lead.status?.is_dead_end),
    editable: true,

    /* Present on `GET /leads/{id}` and absent from the list — `toLeadDetail`
       adds them. Undefined on a list row rather than null, so a caller can tell
       "the list does not carry this" from "the record has no value". */
    campaign: lead.campaign?.name,
    team: lead.team?.name,
    territory: lead.territory?.name,
    website: lead.website,
    mobile: lead.mobile,
    linkedin: lead.linkedin_url,
    annualRevenue: lead.annual_revenue,
    expectedClose: lead.expected_close_date,
    address: lead.address,
    qualification: lead.qualification,
    sla: lead.sla,
    conversion: lead.conversion,
    consent: lead.consent,

    /* Only present when `getLead` asked for them via `include=` — real API
       data, not fixture. `score_breakdown.contributions` is the scoring
       model's own explanation for the number in `score`, not a client-side
       recomputation; `similar` is the duplicate-detection result §4 already
       runs on create, reused here instead of restating it. */
    scoreBreakdown: lead.score_breakdown,
    notes: lead.notes,
    similar: lead.similar,

    raw: lead,
  };
}

/** `{id, row_version}` for the mutation helpers, from a normalised row. */
export const mutationRef = (row) => ({ id: row.id, row_version: row.raw.row_version });
